import React from "react";
import { Link } from "react-router-dom";
import "./jee-main-advanced.css";

const highlights = [
  {
    icon: "school",
    title: "Target Groups",
    desc: "Specially designed batches for Class 11 (Two-year), Class 12 (One-year), and 12th Pass/Repeaters (Dropper batch).",
  },
  {
    icon: "calendar_month",
    title: "Batch Modes",
    desc: "Flexible learning options including intensive Offline Classroom, interactive Live Online, and Hybrid models.",
  },
  {
    icon: "track_changes",
    title: "Key Focus Areas",
    desc: "Building concept depth, personalized exam strategy, regular testing patterns, and detailed performance analysis.",
  },
];

const syllabus = [
  { icon: "science", title: "Physics", body: "Mechanics, Waves, Thermodynamics, Electrostatics, Current Electricity, Magnetism, Optics, Modern Physics, and full exam-oriented practice sets." },
  { icon: "experiment", title: "Chemistry", body: "Physical, Organic, and Inorganic Chemistry with NCERT alignment + advanced JEE patterns, reaction mechanisms, and mixed problem sets." },
  { icon: "calculate", title: "Mathematics", body: "Algebra, Calculus, Coordinate Geometry, Trigonometry, Vectors & 3D, Probability, and advanced multi-concept problem solving." },
];

const advantages = [
  {
    icon: "emoji_events",
    title: "Rank-oriented Material",
    desc: "Curated study material designed by ex-IITians specifically to boost rank.",
  },
  {
    icon: "assignment",
    title: "Regular Mock Tests",
    desc: "Weekly CBT (Computer Based Tests) and offline papers simulating real exam conditions.",
  },
  {
    icon: "insights",
    title: "Performance Tracking",
    desc: "AI-driven analytics to identify weak areas and provide personalized improvement plans.",
  },
  {
    icon: "groups",
    title: "Small Batch Size",
    desc: "Limited students per batch ensuring individual attention from faculty.",
  },
  {
    icon: "workspace_premium",
    title: "Mentorship Program",
    desc: "One-on-one mentorship sessions to manage exam stress and maintain motivation.",
  },
  {
    icon: "local_library",
    title: "24/7 Library Access",
    desc: "Dedicated study zones available for students to practice without distraction.",
  },
];

const JEEMainAdvanced: React.FC = () => {
  const guidanceFormUrl = "https://forms.gle/xdExtHf2Yss4ZaHq9";
  const jeeMainSyllabusUrl =
    "https://drive.google.com/file/d/14qO6XtMvv4aQGv04EjGp7O73TKQ6nTwM/view?usp=sharing";
  const jeeAdvancedSyllabusUrl =
    "https://drive.google.com/file/d/1KYeQ1VtPvZ1mNUGeVPW3gz4ppKNZO5Ji/view?usp=sharing";

  return (
    <div className="jp-page jp-page--jee">
      <div className="jp-container">
        {/* Tabs */}
        <div className="jp-tabs">
          <Link className="jp-tab is-active" to="/courses/jee">
            JEE Preparation
          </Link>
          <Link className="jp-tab" to="/courses/neet">
            NEET Preparation
          </Link>
        </div>

        {/* Hero */}
        <section className="jp-hero jp-hero--jee">
          <div
            className="jp-hero__image"
            style={{ backgroundImage: "url('/mode/jee_course.png')" }}
          />
          <div className="jp-hero__content">
            <span className="jp-badge">
              <span className="material-symbols-outlined">groups</span>
              ENGINEERING WING
            </span>

            <h1 className="jp-hero__title">
              JEE Main &amp; Advanced
              <br />
              Coaching
            </h1>

            <p className="jp-hero__subtitle">
              Focused coaching for engineering aspirants with emphasis on conceptual clarity, rigorous problem-solving, and systematic preparation.
            </p>

            <div className="jp-hero__actions">
              <a className="jp-btn jp-btn--primary" href={guidanceFormUrl} target="_blank" rel="noopener noreferrer">
                Register for  career Guidance
              </a>
              <Link className="jp-btn jp-btn--secondary" to="/our-students">
                View Success Stories
              </Link>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="jp-section">
          <h2 className="jp-section__title">Course Highlights</h2>

          <div className="jp-grid jp-grid--3">
            {highlights.map((h) => (
              <article className="jp-card" key={h.title}>
                <div className="jp-card__icon">
                  <span className="material-symbols-outlined">{h.icon}</span>
                </div>
                <h3 className="jp-card__title">{h.title}</h3>
                <p className="jp-card__text">{h.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Curriculum */}
        <section className="jp-section jp-section--spaced">
          <div className="jp-section__head">
            <div>
              <h2 className="jp-section__title">Curriculum &amp; Syllabus</h2>
              <p className="jp-section__subtitle">Comprehensive coverage of JEE Main &amp; Advanced topics</p>
            </div>

            <a className="jp-link" href={jeeMainSyllabusUrl} target="_blank" rel="noreferrer">
              Download Full JEE Main Syllabus PDF
            </a>
            <a className="jp-link" href={jeeAdvancedSyllabusUrl} target="_blank" rel="noreferrer">
              Download Full JEE Advanced Syllabus PDF
            </a>
          </div>

          <div className="jp-accordion">
            {syllabus.map((s) => (
              <details className="jp-accordion__item" key={s.title}>
                <summary className="jp-accordion__summary">
                  <span className="jp-accordion__left">
                    <span className="material-symbols-outlined">{s.icon}</span>
                    {s.title}
                  </span>
                  <span className="material-symbols-outlined jp-accordion__chev">expand_more</span>
                </summary>
                <div className="jp-accordion__body">
                  <p>{s.body}</p>
                </div>
              </details>
            ))}
          </div>

          <div className="jp-note">
            <span className="material-symbols-outlined">info</span>
            <div>
              <strong>Note:</strong> All courses include Application-based learning modules and daily Doubt-clearing sessions.
            </div>
          </div>
        </section>

        {/* Advantage */}
        <section className="jp-section jp-section--spaced">
          <h2 className="jp-section__title">The S-CUBUS Advantage</h2>

          <div className="jp-grid jp-grid--3">
            {advantages.map((a) => (
              <article className="jp-adv" key={a.title}>
                <div className="jp-adv__icon">
                  <span className="material-symbols-outlined">{a.icon}</span>
                </div>
                <div>
                  <h3 className="jp-adv__title">{a.title}</h3>
                  <p className="jp-adv__text">{a.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="jp-cta">
          <div className="jp-cta__content">
            <h2 className="jp-cta__title">Ready to ace your exams?</h2>
            <p className="jp-cta__text">
              Join the league of toppers today. Limited seats available for upcoming batches.
            </p>
          </div>

          <div className="jp-cta__actions">
            <a className="jp-btn jp-btn--primary" href={guidanceFormUrl} target="_blank" rel="noopener noreferrer">
              Register for Career Guidance
            </a>
            <button className="jp-btn jp-btn--ghost">
              <span className="material-symbols-outlined">download</span>
              Brochure
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default JEEMainAdvanced;
